<template>
    <div>
        <ApolloQuery :query="myQuery" :variables="getVariables" @loading="isSearching" ref="appolloQuery">
            <template v-slot="{ result: { data, error }, query}">
                <!-- Displaying error from GraphQL -->
                <section v-if="error">
                    <div class="api-errors alert alert-danger text-center" role="alert">
                        {{ getErrorMessage(error) }}
                    </div>
                </section>

                <section v-if="isInvalidPageNumber">
                    <div class="api-errors alert alert-danger text-center" role="alert">
                        Error: {{ currentPage }} is an invalid page number.
                    </div>
                </section>

                <!-- Main section to present data retrieved from GraphQL -->
                <section v-if="data && !error && !isInvalidPageNumber" class="container-fluid" id="main-section">
                    <div class="row">
                        <div class="col-md-6 table-browser-remove-left-padding">
                            <search
                                :initial-search-text="userSearchText"
                                @update-search="updateSearch"
                                @reset-pagination="resetPagination"
                            />
                        </div>
                        <div class="col-md-6">
                            <button type="button" class="btn btn-default" @click="toggleFilterOption()">
                                <i class="fa fa-filter" aria-hidden="true" /> Filters
                            </button>

                            <button type="button" class="btn btn-default" @click="toggleTableSettingModal()">
                                <i class="fa fa-cog" aria-hidden="true" /> Settings
                            </button>
                            <div class="pull-right text-right" v-if="!searching && data[operationName].edges">
                                <page-range
                                    :page-length="pageLength"
                                    :current-page="currentPage"
                                    :total-count="data[operationName].totalCount"
                                    @isInvalidPageNumber="isInvalidPageNumber=true"
                                />
                            </div>
                        </div>
                        <table-setting
                            v-if="tableSettingModalOpened"
                            ref="table-setting"
                            :initial-modal-opened="tableSettingModalOpened"
                            :initial-page-length="pageLength"
                            :initial-visible-column-options="visibleColumnOptions"
                            :initial-visible-detail-options="visibleDetailOptions"
                            :initial-alternative-name-setting="alternativeNameSetting"
                            :alternative-name-types="requiredProps.alternativeNameTypes"
                            :init-show-federated="showFederated"
                            @close-modal="toggleTableSettingModal"
                            @apply-settings="applyTableSettings"
                            @apply-default-settings="applyDefaultSettings"
                        />
                        <span>
                             <!--Hidden checkbox when allow user to select all item, include those not shown within the page-->
                            <input type="checkbox" ref="all_in_queryset" name="all_in_queryset"
                                   class="select-all-compatible table-browser-hide-element"
                            >
                            <input type="hidden" name="qs" :value="data[operationName].allQsUuid">
                        </span>
                    </div>

                     <!--Table to display metadata list-->
                    <div class="row mt-3" ref="tableWrapper">
                        <table class="table table-browser-table" :style="tableWidth">
                            <thead class="table-browser-sticky-header">
                                 <tr>
                                     <th class="table-browser-bulk-action-col-fixed-width" v-if="canViewBulkAction">
                                         <div>
                                             <div class="table-browser-inline-block">
                                                 <!-- Checkbox to allow selection of all items in the page -->
                                                 <!-- Need to prevent enter key from submitting the bulk action form-->
                                                 <input type="checkbox"
                                                        @change="updatePartialSelect($event.target.checked)"
                                                        ref="partial-select"
                                                        @keypress.13.prevent
                                                 >
                                             </div>
                                             <div class="table-browser-inline-block">
                                                 <bulk-action :bulk-action-options="extraInfo.bulkActionOptions" />
                                             </div>
                                         </div>
                                     </th>

                                     <th>
                                         Name
                                         <sort header-name="name" :initial-order="order" @update-sort="updateSort" />
                                     </th>

                                     <th v-if="visibleColumnOptions.shortDefinition.selected">
                                         Details
                                     </th>

                                     <th v-if="visibleColumnOptions.status.selected">
                                         Status
                                     </th>

                                     <th v-if="visibleColumnOptions.modified.selected">
                                         Last Updated
                                         <sort header-name="modified" :initial-order="order" @update-sort="updateSort" />
                                     </th>

                                     <th v-if="visibleColumnOptions.created.selected">
                                         Created On
                                         <sort header-name="created" :initial-order="order" @update-sort="updateSort" />
                                     </th>

                                     <th v-if="showActionsDropdown">
                                         Item Actions
                                     </th>

                                     <th v-if="visibleColumnOptions.tag.selected">
                                         Tags
                                     </th>
                                 </tr>
                             </thead>

                            <tbody>
                                 <!-- This row is for filters -->
                                 <transition name="table-browser-filter-option-slide">
                                     <tr v-if="filterOptionsShown">
                                         <td v-if="canViewBulkAction">
                                             <div>
                                                 <filter-option
                                                     :initial-multiple-selected-option="multipleSelectedItemControlStatus"
                                                     :options="itemControlStatus"
                                                     :total-visible-table-columns="totalVisibleColumnNumber"
                                                     @update-selected-option="updateItemControlStatusOption"
                                                 />
                                             </div>
                                         </td>
                                         <td>
                                             <div v-if="canFilterByType">
                                                 <filter-option
                                                     :initial-multiple-selected-option="multipleSelectedTypes"
                                                     :options="requiredProps.typeOptions"
                                                     :total-visible-table-columns="totalVisibleColumnNumber"
                                                     @update-selected-option="updateTypeOption"
                                                 />
                                             </div>
                                         </td>
                                         <td v-if="visibleColumnOptions.shortDefinition.selected" />
                                         <td v-if="visibleColumnOptions.status.selected">
                                             <div>
                                                  <filter-option
                                                      :initial-multiple-selected-option="multipleSelectedStatus"
                                                      :options="requiredProps.stateOptions"
                                                      :total-visible-table-columns="totalVisibleColumnNumber"
                                                      @update-selected-option="updateStateOption"
                                                  />
                                             </div>
                                         </td>
                                         <td v-if="visibleColumnOptions.modified.selected" />
                                         <td v-if="visibleColumnOptions.created.selected" />
                                         <td v-if="showActionsDropdown" />
                                         <td v-if="visibleColumnOptions.tag.selected" />
                                     </tr>
                                 </transition>

                                 <!-- This row to tell user GraphQL returns zero items -->
                                 <tr class="text-center" v-if="!data[operationName].totalCount">
                                     <td :colspan="totalVisibleColumnNumber">
                                         No results found
                                     </td>
                                 </tr>

                                 <!-- This row contains helper text when checkbox is ticked-->
                                 <tr v-if="hasSelected">
                                     <td :colspan="totalVisibleColumnNumber" class="text-center">
                                         <bulk-action-helper-text
                                             ref="bulk-action-helper-text"
                                             :verbose-operation-name="verboseOperationName"
                                             :page-length="pageLength"
                                             :total-count="data[operationName].totalCount"
                                             :total-selected-items="totalSelectedItems"
                                             @select-all-items="selectAll(data[operationName].totalCount)"
                                             @clear-selections="clearSelections"
                                         />
                                     </td>
                                 </tr>

                                 <!-- This row onwards are the content -->
                                 <template v-if="!searching && data[operationName]">
                                     <tr v-for="item in data[operationName].edges" :key="item.id">
                                         <td v-if="canViewBulkAction">
                                             <div>
                                                 <div class="table-browser-inline-block">
                                                     <!-- Need to prevent enter key from submitting the bulk action form-->
                                                     <input
                                                         type="checkbox" name="items" class="v-select-all-compatible"
                                                         ref="item-checkboxes"
                                                         :id="getCustomId(item.node.aristotleId)"
                                                         :value="item.node.aristotleId"
                                                         @change="updateSingleSelect($event.target.checked, data[operationName].edges.length)"
                                                         @keypress.13.prevent
                                                     >
                                                 </div>
                                                 <div class="table-browser-inline-block">
                                                     <toggle-favourite
                                                         v-if="item.node.favouriteInfo"
                                                         :item-uuid="item.node.uuid"
                                                         :initial-favourite-info="item.node.favouriteInfo"
                                                         :base-end-point="extraInfo.favouriteBaseEndPoint"
                                                         @update-favourite-info="updateFavoriteInfo"
                                                     />
                                                 </div>
                                                 <div class="table-browser-inline-block">
                                                     <i v-if="item.node.statuses.length" class="fa fa-university fa-fw" title="Registered" />
                                                     <i v-else class="fa fa-flag-o fa-fw" title="In progress" />
                                                 </div>
                                                 <div class="table-browser-inline-block">
                                                     <i v-if="item.node.isFederated" class="fa fa-chart-network fa-fw" title="Federated" />
                                                 </div>
                                             </div>
                                         </td>

                                         <td>
                                             <metadata-name
                                                 :item="item.node"
                                                 :alternative-name-setting="alternativeNameSetting"
                                                 :is-shared-sandbox-view="isSharedSandboxView"
                                                 :shared-item-end-point="extraInfo.sharedItemEndPoint"
                                             />
                                         </td>

                                         <td v-if="visibleColumnOptions.shortDefinition.selected">
                                             <metadata-detail
                                                 :query-operation="operationName"
                                                 :item="item.node"
                                                 :visible-detail-option="visibleDetailOptions"
                                             />
                                         </td>

                                         <td v-if="visibleColumnOptions.status.selected">
                                             <metadata-status :statuses="item.node.statuses" />
                                         </td>

                                         <td v-if="visibleColumnOptions.modified.selected">
                                             {{ getPrettyDateTime(item.node.modified) }}
                                         </td>

                                         <td v-if="visibleColumnOptions.created.selected">
                                             {{ getPrettyDateTime(item.node.created) }}
                                         </td>

                                         <td v-if="showActionsDropdown">
                                             <item-action
                                                 :item="item.node"
                                                 :extra-info="extraInfo"
                                                 :is-delete-view="isDeleteView"
                                                 @removed-from-workgroup="query.refetch()"
                                             />
                                         </td>

                                         <td v-if="visibleColumnOptions.tag.selected">
                                             <metadata-tag :tag-info="item.node.tagInfo" />
                                         </td>
                                     </tr>
                                 </template>
                            </tbody>
                        </table>
                     </div>

                    <!-- This is a loading icon when loading new queries -->
                    <div v-if="data && searching" class="text-center">
                        <i class="fa fa-spinner fa-spin" /> Loading...
                    </div>

                    <!-- Handles bulk actions -->
                    <div class="row mt-3">
                        <bulk-action
                            v-if="canViewBulkAction"
                            :bulk-action-options="extraInfo.bulkActionOptions"
                            :reversed="true"
                        />
                    </div>

                    <!-- Handles pagination -->
                    <pagination
                        v-if="!searching && data[operationName] && !isInvalidPageNumber"
                        ref="pagination"
                        v-model="currentPage"
                        :total-page="data[operationName].pageCount"
                        align="center"
                        boundary-links
                        @change="(pageIndex) => changePage(pageIndex)"
                    />
                </section>

                <!-- This is a loading icon when requesting for data the first time -->
                <section v-if="!data" id="loading-section">
                    <div class="text-center">
                        <i class="fa fa-spinner fa-spin" /> Loading...
                    </div>
                </section>
            </template>
        </ApolloQuery>
    </div>
</template>

<script>
import gql from 'graphql-tag'
import {formatDateTime, anyCaseToCapitalisedSentenceCase} from "src/lib/utils";
import {
    queryTemplateGeneric,
    queryDataElements,
    queryValueDomains,
    queryDataElementConcepts,
    queryUnitOfMeasures,
    queryMetadata,
    querySandboxMetadata,
    queryRelatedMetadata,
    queryRelatedMetadataByType
} from 'src/lib/graphql/queries'

import BulkAction from "@/browse/BulkAction.vue"
import BulkActionHelperText from "@/browse/BulkActionHelperText.vue"
import FilterOption from "@/browse/FilterOption.vue"
import ItemAction from "@/browse/ItemAction.vue"
import MetadataDetail from "@/browse/MetadataDetail.vue"
import MetadataName from "@/browse/MetadataName.vue"
import MetadataStatus from "@/browse/MetadataStatus.vue"
import MetadataTag from "@/browse/MetadataTag.vue"
import PageRange from "@/browse/PageRange.vue"
import Search from "@/browse/Search.vue"
import Sort from "@/browse/Sort.vue"
import TableSetting from "@/browse/TableSetting.vue"
import ToggleFavourite from "@/browse/ToggleFavourite.vue"
import Pagination from 'uiv/src/components/pagination/Pagination.vue'

export default {
    name: "MetadataList",
    components: {
        BulkAction,
        BulkActionHelperText,
        FilterOption,
        ItemAction,
        MetadataDetail,
        MetadataName,
        MetadataStatus,
        MetadataTag,
        PageRange,
        Search,
        Sort,
        TableSetting,
        ToggleFavourite,
        Pagination,
    },

    props: {
        requiredPropsJson: {
            type: String,
            required: true,
        },

        extraInfoJson: {
            type: String,
            default: ''
        },
    },

    data() {
        return {
            cursor: '',
            pageLength: 0,
            currentPage: 1,
            isInvalidPageNumber: false,
            userSearchText: '', // The content the user has entered
            nameSearch: '', // The text to send in the GraphQL query if name search is selected.
            definitionSearch: '', // The text to send in the GraphQL query if definition search is selected.
            allFieldsSearch: '', // The text to send in the GraphQL query if full-text search is selected.
            order: 'name',
            filterOptionsShown: false,
            tableSettingModalOpened: false,
            searching: false,
            hasSelected: false,
            totalSelectedItems: 0,
            totalSelectedItemsTemporary: 0,
            visibleColumnOptions: {},
            totalVisibleColumnNumber: 0,
            visibleDetailOptions: {},
            tableWidth: {width: "100%"},
            wrapperDom: {},
            alternativeNameSetting: {},
            showFederated: false,
            myQuery: '',
            extraInfo: {},
            requiredProps: {},
            multipleSelectedTypes: '',
            multipleSelectedStatus: '',
            multipleSelectedItemControlStatus: '',
            itemControlStatus: [
                ['favourited', 'Favourited', 'fa fa-bookmark fa-fw'],
                ['registered', 'Registered', 'fa fa-university fa-fw'],
                ['federated', 'Federated', 'fa fa-chart-network fa-fw']
            ]
        }
    },

    computed: {
        // Examples of operationName are "metadata", "sandboxMetadata", "objectClasses" and etc.
        operationName() {
            return this.requiredProps.operationName
        },

        showActionsDropdown() {
            return this.visibleColumnOptions.action.selected && this.requiredProps.showActionsDropDown
        },

        // Get variables for graphQL query, better to pass all variables as a single object
        getVariables() {
            return {
                first: this.pageLength,
                after: this.cursor,
                name_Icontains: this.nameSearch,
                definition_Icontains: this.definitionSearch,
                search: this.allFieldsSearch,
                orderBy: this.order,
                statusIn: this.multipleSelectedStatus,
                stewardshipOrganisation: this.extraInfo.stewardshipOrganisationUuid,
                workgroupId: this.extraInfo.workgroupId,
                favouritedOrTagged: this.extraInfo.favouritedOrTagged,
                favourited: this.extraInfo.favourited,
                tagId: this.extraInfo.tagId,
                metadataTypeIn: this.multipleSelectedTypes,
                itemControlStatusIn: this.multipleSelectedItemControlStatus,
                showFederated: this.showFederated,
                sharedUuid: this.extraInfo.sharedUuid,
                isDeleted: this.extraInfo.isDeleted,
                relatedToMetadata: this.extraInfo.relatedToMetadata,
                relatedToMetadataRelationType: this.extraInfo.relatedToMetadataRelationType,
                collection: this.extraInfo.filterInCollection,
                subcollections: this.extraInfo.filterInSubcollections,
            }
        },

        // Convert camel case modal name to title case
        verboseOperationName() {
            if (this.operationName == 'metadata' || this.operationName == 'sandboxMetadata') {
                return 'items'
            }
            return anyCaseToCapitalisedSentenceCase(this.operationName)
        },

        // Only allow filtering by type if viewing a list of all metadata
        canFilterByType() {
            return this.operationName === "metadata" || this.operationName === "sandboxMetadata"
        },

        isDeleteView() {
            if (Object.prototype.hasOwnProperty.call(this.extraInfo, 'isDeleted')) {
                return this.extraInfo.isDeleted
            }
            return false
        },

        isSharedSandboxView() {
            return Object.prototype.hasOwnProperty.call(this.extraInfo, 'sharedUuid')
        },

        canViewBulkAction() {
            return !this.isDeleteView && !this.isSharedSandboxView
        }
    },

    watch: {
        // Save filters to both query string and localStorage
        multipleSelectedStatus: function () {
            localStorage.setItem('registrationStatus', this.multipleSelectedStatus)
            this.setQueryString()
            this.resetPagination()
        },

        multipleSelectedItemControlStatus: function () {
            localStorage.setItem('itemControlStatus', this.multipleSelectedItemControlStatus)
            this.setQueryString()
            this.resetPagination()
        },

        multipleSelectedTypes: function () {
            localStorage.setItem('metadataType', this.multipleSelectedTypes)
            this.setQueryString()
            this.resetPagination()
        },

        // Save settings to localStorage
        visibleColumnOptions: function () {
            localStorage.setItem('visibleColumnOptions', JSON.stringify(this.visibleColumnOptions))
            this.setTotalVisibleColumnNumber()
        },

        visibleDetailOptions: function () {
            localStorage.setItem('visibleDetailOptions', JSON.stringify(this.visibleDetailOptions))
        },

        pageLength: function () {
            localStorage.setItem('pageLength', this.pageLength)
        },

        currentPage: function () {
            this.setQueryString()
        },

        showFederated: function () {
            localStorage.setItem('showFederated', this.showFederated)
        },
    },

    beforeMount() {
        // Set required props from json, these are the common props for all pages
        this.setRequiredProps()
        // Set registrations status from query string, browser local storage or default value
        this.setRegistrationStatusFilter()
        // Set metadata type settings from query string, browser local storage or default value
        this.setMetadataTypeFilter()
        // Set item control status settings from query string, browser local storage or default value
        this.setItemControlStatusFilter()
        // Set visible column options from browser local storage or default value
        this.setVisibleColumnOptionsSetting()
        // Set visible column options from browser local storage or default value
        this.setVisibleDetailOptionsSetting()
        // Set page length from browser local storage or default value
        this.setPageLengthSetting()
        // Set page number from url query string
        this.setPageNumber()
        // Set showFederated option
        this.setShowFederated()
        // Set extra info, different page requires different extra info
        this.setExtraInfo()
        // Set query base on metadata type
        this.setQuery()
    },

    mounted() {
        // Added listener to watch resize even so that table size can be calculated accordingly
        window.addEventListener('resize', this.onResize)

        // Need to resize table when there are more than 4 columns needs to be display at the start
        this.$watch("$refs.appolloQuery.result", () => {
            this.updateTableWidth()
        })

        const q = this.$route.query.q
        if (q) {
            this.nameSearch = q
        }
    },

    beforeDestroy() {
        // Unregister the event listener before destroying this Vue instance
        window.removeEventListener('resize', this.onResize)
    },

    methods: {
        // Update the page when single item checkbox is selected or unselected
        updateSingleSelect: function (bool, pageItemsCount) {
            if (bool) {
                this.increaseCount()

                // When all items on the page has been selected
                if (this.totalSelectedItems == pageItemsCount) {
                    this.$refs["partial-select"].checked = true
                }
            } else {
                this.decreaseCount()

                // When all items on the page has not been selected
                if (this.totalSelectedItems != pageItemsCount) {
                    this.$refs["partial-select"].checked = false
                }
            }

            this.renderHelperText()
        },

        // Update the page when all items on the page are selected or unselected
        updatePartialSelect: function (bool) {
            // Get all checkboxes in the table in the current page
            for (let checkbox of this.getCheckBoxes()) {
                if (checkbox.checked !== bool) {
                    checkbox.checked = bool
                    if (bool) {
                        this.increaseCount()
                    } else {
                        this.decreaseCount()
                    }
                }
            }

            this.renderHelperText()
        },

        // Check and uncheck the hidden checkbox to allow selection of all items, literally all items
        selectAll: function (totalCount) {
            this.toggleCheckboxesState()
            this.$refs["all_in_queryset"].checked = !this.$refs["all_in_queryset"].checked
            this.totalSelectedItems = totalCount
        },

        // Helper function to set/remove the attribute 'disabled'
        toggleDisabledAttribute: function (element) {
            if (element.hasAttribute('disabled')) {
                element.removeAttribute('disabled')
            } else {
                element.setAttribute('disabled', 'disabled')
            }
        },

        // Disable/enable all visible checkbox on the page
        toggleCheckboxesState: function () {
            // Need to include the partial-select checkbox
            let partialSelectCheckbox = this.$refs["partial-select"]
            this.toggleDisabledAttribute(partialSelectCheckbox)

            for (let checkbox of this.getCheckBoxes()) {
                this.toggleDisabledAttribute(checkbox)
            }
        },

        // Clear all the selections, currently only being used when switching to a different items page or reset to page 1
        clearSelections: function () {
            if (typeof this.$refs.all_in_queryset !== "undefined" && typeof this.$refs["partial-select"] !== "undefined") {
                if (this.$refs["all_in_queryset"].checked) {
                    this.toggleCheckboxesState()
                }
                this.$refs["partial-select"].checked = false
                this.$refs["all_in_queryset"].checked = false
                this.updatePartialSelect(false)
                this.clearCount()
                this.renderHelperText()
            }
        },

        // Get all the checkboxes in the table of items
        getCheckBoxes: function () {
            return this.$refs["item-checkboxes"]
        },

        // Increase count when a checkbox is checked
        increaseCount: function () {
            this.totalSelectedItems += 1
        },

        // Decrease count when a checkbox is unchecked
        decreaseCount: function () {
            if (this.totalSelectedItems) {
                this.totalSelectedItems -= 1
            }
        },

        // Reset count to 0, currently only being used when switching to a different items page
        clearCount: function () {
            this.totalSelectedItems = 0
        },

        // Rendering helper text depending on if all items on the page has been selected
        renderHelperText: function () {
            // When
            if (this.$refs["partial-select"].checked) {
                this.hasSelected = true
            } else {
                this.hasSelected = false
            }
        },

        // Action to navigate to a different paginated page
        changePage: function (pageIndex) {
            // If user is changing page number via the url query string, need to update current page value
            if (this.currentPage != pageIndex) {
                this.currentPage = pageIndex
            }

            // Cursor is not needed when switching to the first page
            if (pageIndex == 1) {
                this.cursor = ''
            } else {
                // Cursor used in graphQL is encoded using Base64
                const metadata_id = (pageIndex - 1) * this.pageLength - 1
                this.cursor = btoa("arrayconnection:" + metadata_id.toString())
            }

            // Need to clear all checked checkboxes
            this.clearSelections()
        },

        // Helper function that return custom id in the format that is required when using bulk action
        getCustomId: (id) => {
            return "id_items_" + id
        },

        // This allow access to imported methods
        getPrettyDateTime(time) {
            return formatDateTime(time)
        },

        // Update search for
        updateSearch(obj) {
            this.nameSearch = ''
            this.definitionSearch = ''
            this.allFieldsSearch = ''
            if (obj.searchOn == "name") {
                this.nameSearch = obj.searchText
                this.allFieldsSearch = null
            }
            else if (obj.searchOn == "allfields") {
                this.allFieldsSearch = obj.searchText
            }
            else {
                this.definitionSearch = obj.searchText
                this.allFieldsSearch = null
            }
        },

        // Get loading status from Apollo Client
        isSearching(isLoading) {
            this.searching = isLoading
        },

        // Methods for settings
        toggleTableSettingModal: function () {
            this.tableSettingModalOpened = !this.tableSettingModalOpened
        },

        applyTableSettings: function (options) {
            this.resetPagination()
            this.pageLength = parseInt(options.pageLength)
            this.visibleColumnOptions = options.visibleColumns
            this.visibleDetailOptions = options.visibleDetailOptions
            this.alternativeNameSetting = options.alternativeNameSetting
            this.showFederated = options.showFederated
            this.updateTableWidth()
        },

        applyDefaultSettings: function () {
            this.resetPagination()
            this.pageLength = 20
            this.visibleColumnOptions = this.requiredProps.visibleColumnOptions
            this.visibleDetailOptions = this.requiredProps.visibleDetailOptions
            this.alternativeNameSetting = {}
            this.showFederated = false
            this.multipleSelectedTypes = ''
            this.multipleSelectedStatus = ''
            this.multipleSelectedItemControlStatus = ''
            this.updateTableWidth()
        },

        updateSort(args) {
            if (args.order.includes('name') && this.alternativeNameSetting.included) {
                this.order = args.order + '?' + this.alternativeNameSetting.selected
            } else {
                this.order = args.order
            }
        },

        // Reset pagination when performing actions like searching, filtering or change table settings.
        resetPagination() {
            if (typeof this.$refs.pagination !== "undefined") {
                this.$refs.pagination.onPageChange(1)
                this.clearSelections()
            }
        },

        setVisibleColumnOptionsSetting: function () {
            if (localStorage.getItem('visibleColumnOptions')) {
                this.visibleColumnOptions = JSON.parse(localStorage.getItem('visibleColumnOptions'))
            } else {
                // The variable "visibleColumnOptions" is needed when rendering template
                this.visibleColumnOptions = this.requiredProps.visibleColumnOptions
            }
        },

        setVisibleDetailOptionsSetting: function () {
            if (localStorage.getItem('visibleDetailOptions')) {
                this.visibleDetailOptions = JSON.parse(localStorage.getItem('visibleDetailOptions'))
            } else {
                // The variable "visibleDetailOptions" is needed when rendering template
                this.visibleDetailOptions = this.requiredProps.visibleDetailOptions
            }
        },

        setPageLengthSetting: function () {
            if (localStorage.getItem('pageLength')) {
                this.pageLength = parseInt(localStorage.getItem('pageLength'))
            } else {
                this.pageLength = 20
            }
        },

        setShowFederated: function() {
            if (localStorage.getItem('showFederated')) {
                this.showFederated = (localStorage.getItem('showFederated') === 'true')
            }
        },

        // Methods for filters
        toggleFilterOption: function () {
            this.filterOptionsShown = !this.filterOptionsShown
        },

        updateTypeOption: function (args) {
            this.multipleSelectedTypes = args.multipleSelectedOption.join(',')
        },

        updateStateOption(args) {
            this.multipleSelectedStatus = args.multipleSelectedOption.join(',')
        },

        updateItemControlStatusOption: function (args) {
            this.multipleSelectedItemControlStatus = args.multipleSelectedOption.join(',')
        },

        // For url query string
        setQueryString() {
            if (this.canFilterByType) {
                // "NavigationDuplicated" error will raise if the url is the same when using $router.push,
                // therefore, need to catch it
                this.$router.push(this.createQueryString(true)).catch(() => {
                })
            } else {
                this.$router.push(this.createQueryString(false)).catch(() => {
                })
            }
        },

        // For url query string
        createQueryString(includeMetadataType) {
            let queryString = {query: {}}
            if (includeMetadataType) {
                if (this.multipleSelectedTypes) {
                    queryString.query.metadataType = this.multipleSelectedTypes.split(",")
                }
            }

            if (this.multipleSelectedStatus) {
                queryString.query.registrationStatus = this.multipleSelectedStatus.split(",")
            }

            if (this.multipleSelectedItemControlStatus) {
                queryString.query.itemControlStatus = this.multipleSelectedItemControlStatus.split(",")
            }

            if (this.currentPage !== 1) {
                queryString.query.page = this.currentPage
            }

            queryString.query.showFederated = this.showFederated

            return queryString
        },

        setRegistrationStatusFilter: function () {
            // Get value from query string first
            if (this.$route.query.registrationStatus) {
                this.filterOptionsShown = true
                if (Array.isArray(this.$route.query.registrationStatus)) {
                    this.multipleSelectedStatus = this.$route.query.registrationStatus.join(',')
                } else {
                    this.multipleSelectedStatus = this.$route.query.registrationStatus
                }
            }

            // Then check values in browser localstorage.
            else if (localStorage.getItem('registrationStatus') && this.isValidFilterOptions(localStorage.getItem('registrationStatus'), this.requiredProps.stateOptions)) {
                this.filterOptionsShown = true
                this.multipleSelectedStatus = localStorage.getItem('registrationStatus')
            }

            // Finally, if none found, use a default value
            else {
                this.multipleSelectedStatus = ''
            }
        },

        setItemControlStatusFilter: function () {
            if (this.$route.query.itemControlStatus) {
                this.filterOptionsShown = true
                if (Array.isArray(this.$route.query.itemControlStatus)) {
                    this.multipleSelectedItemControlStatus = this.$route.query.itemControlStatus.join(',')
                } else {
                    this.multipleSelectedItemControlStatus = this.$route.query.itemControlStatus
                }
            } else if (localStorage.getItem('itemControlStatus') && this.isValidFilterOptions(localStorage.getItem('itemControlStatus'), this.itemControlStatus)) {
                this.filterOptionsShown = true
                this.multipleSelectedItemControlStatus = localStorage.getItem('itemControlStatus')
            } else {
                this.multipleSelectedItemControlStatus = ''
            }
        },

        setMetadataTypeFilter: function () {
            // Only page that shows list of all metadata can be filtered by type
            if (this.$route.query.metadataType && this.canFilterByType) {
                this.filterOptionsShown = true
                if (Array.isArray(this.$route.query.metadataType)) {
                    this.multipleSelectedTypes = this.$route.query.metadataType.join(',')
                } else {
                    this.multipleSelectedTypes = this.$route.query.metadataType
                }
            } else if (localStorage.getItem('metadataType') && this.canFilterByType && this.isValidFilterOptions(localStorage.getItem('metadataType'), this.requiredProps.typeOptions)) {
                this.filterOptionsShown = true
                this.multipleSelectedTypes = localStorage.getItem('metadataType')
            } else {
                this.multipleSelectedTypes = ""
            }
        },

        setPageNumber: function () {
            const queryPageNumber = parseInt(this.$route.query.page)
            // Only accepts integer when switching page via the url query string
            if (Number.isInteger(queryPageNumber)) {
                this.changePage(queryPageNumber)
            } else {
                this.changePage(1)
            }
        },

        isValidFilterOptions: function (selectedOptionsString, validOptions) {
            // Selected filter options is being passed to GraphQL as a comma separated string
            const selectedOptions = selectedOptionsString.split(',')

            // Each option in the valid filter options has two values. Eg [objectClasses, Object Class]
            // Only the first value in each option is needed in this use case
            let cleanValidOptions = []
            for (let i = 0; i < validOptions.length; i++) {
                cleanValidOptions.push(validOptions[i][0])
            }

            // If selectedOptions is a sub array in cleanValidOption, then all values in selectedOptions are valid.
            return selectedOptions.every(val => cleanValidOptions.includes(val))
        },

        // Methods to customise table size
        onResize() {
            let tableWrapperWidth = this.$refs.tableWrapper.clientWidth

            // Removes the 'px' characters
            let tableWidth = parseInt(this.tableWidth.width.slice(0, -2))

            // Update table width if smaller than the wrapper width
            if (tableWidth < tableWrapperWidth) {
                this.updateTableWidth()
            }
        },

        setTotalVisibleColumnNumber: function () {
            let col_no = 0
            if (this.extraInfo.bulkActionOptions) {
                col_no += 1
            }
            for (let c in this.visibleColumnOptions) {
                if (this.visibleColumnOptions[c].selected) {
                    col_no += 1
                }
            }
            this.totalVisibleColumnNumber = col_no
        },

        updateTableWidth: function () {
            const defaultColumnNumber = 7
            let colCounter = 0
            for (let c in this.visibleColumnOptions) {
                if (this.visibleColumnOptions[c].selected) {
                    colCounter += 1
                }
            }
            // Reset width to 100%
            this.tableWidth.width = '100%'

            // Only modified the table width when number of columns to be displayed is higher than defaultColumnNumber
            if (colCounter > defaultColumnNumber) {
                let wrapperWidth = this.$refs["tableWrapper"].clientWidth
                let newWidth = (wrapperWidth / defaultColumnNumber) * colCounter
                this.tableWidth.width = newWidth + 'px'
            }
        },

        setQuery: function () {
            // Update gql query templates base on metadata type
            switch (this.operationName) {
                case 'dataElements':
                    this.myQuery = gql(queryDataElements)
                    break
                case 'valueDomains':
                    this.myQuery = gql(queryValueDomains)
                    break
                case 'dataElementConcepts':
                    this.myQuery = gql(queryDataElementConcepts)
                    break
                case 'unitOfMeasures':
                    this.myQuery = gql(queryUnitOfMeasures)
                    break
                case 'metadata':
                    if (this.extraInfo.relatedToMetadata) {
                        if (this.extraInfo.relatedToMetadataRelationType) {
                            this.myQuery = gql(queryRelatedMetadataByType)
                        } else {
                            this.myQuery = gql(queryRelatedMetadata)
                        }
                    } else {
                        this.myQuery = gql(queryMetadata)
                    }
                    break
                case 'sandboxMetadata':
                    this.myQuery = gql(querySandboxMetadata)
                    break
                default:
                    this.myQuery = gql(queryTemplateGeneric.replace(/operationName/g, this.operationName))
            }
        },

        setExtraInfo: function () {
            if (this.extraInfoJson) {
                this.extraInfo = JSON.parse(this.extraInfoJson)
            }
        },

        setRequiredProps: function () {
            if (this.requiredPropsJson) {
                this.requiredProps = JSON.parse(this.requiredPropsJson)
            }
        },

        updateFavoriteInfo: function (options) {
            // Get apollo client
            const client = this.$apollo.provider.defaultClient

            // Get cached query from apollo client
            let cachedQuery = client.readQuery({
                    query: this.myQuery,
                    variables: this.getVariables
                }
            )

            // Search by item's uuid from the cached query
            for (let i = 0; i < cachedQuery[this.operationName].edges.length; i++) {
                if (cachedQuery[this.operationName].edges[i].node.uuid == options.itemUuid) {
                    // If item found, update the item's favouriteInfo in the cached query with the new favouriteInfo
                    cachedQuery[this.operationName].edges[i].node.favouriteInfo = options.favouriteInfo
                }
            }

            // Saved the updated cached query
            client.writeQuery({
                query: this.myQuery,
                variables: this.getVariables,
                data: cachedQuery
            })
        },

        getErrorMessage(error) {
            const defaultMessage = "Error loading metadata. Please try again."
            if (!error) {
                return defaultMessage
            }

            // Apollo network errors can include either `statusCode` or `status`.
            const networkError = error.networkError || {}
            const statusCode = networkError.statusCode || networkError.status

            if (statusCode === 504) {
                return "The metadata request timed out (504). Please try again, narrow filters, or use a smaller page size."
            }

            if (statusCode === 502 || statusCode === 503) {
                return `The metadata service is temporarily unavailable (${statusCode}). Please try again shortly.`
            }

            if (statusCode === 401 || statusCode === 403) {
                return "Your session may have expired or access is denied. Please refresh the page and sign in again."
            }

            if (networkError.message) {
                return `Error loading metadata: ${networkError.message}`
            }

            if (error.message) {
                return `Error loading metadata: ${error.message}`
            }

            return defaultMessage
        },
    },
}
</script>
